import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue3-toastify';

/**
 * Hooks simples para operações CRUD
 *
 * Exemplo:
 * const createMutation = useCrudCreate('inverters', 'Inversor', service.create);
 */

export function useCrudCreate<TData, TResponse>(
  queryKey: string,
  entityName: string,
  createFn: (_data: TData) => Promise<TResponse>,
  customErrorHandler?: (_error: any) => string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFn,
    onError: (error: any) => {
      const defaultMessage = error?.response?.data?.message || `Erro ao cadastrar ${entityName}`;
      const message = customErrorHandler ? customErrorHandler(error) : defaultMessage;
      toast.error(message);
    },
    onSuccess: (_data: TResponse) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      queryClient.invalidateQueries({ queryKey: ['brands', queryKey] });
      toast.success(`${entityName} cadastrado com sucesso!`);
    },
    retry: 3,
  });
}

export function useCrudDelete(
  queryKey: string,
  entityName: string,
  deleteFn: (_id: number) => Promise<any>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFn,
    onError: (error: any) => {
      const message = error?.response?.data?.message || `Erro ao excluir ${entityName}`;
      toast.error(message);
    },
    onSuccess: (_data: any, _deletedId: number) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`${entityName} excluído com sucesso!`);
    },
    retry: 3,
  });
}

export function useCrudUpdate<TData, TResponse>(
  queryKey: string,
  entityName: string,
  updateFn: (_id: number, _data: TData) => Promise<TResponse>,
  customErrorHandler?: (_error: any) => string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, id }: { data: TData; id: number }) => updateFn(id, data),
    onError: (error: any) => {
      const defaultMessage = error?.response?.data?.message || `Erro ao atualizar ${entityName}`;
      const message = customErrorHandler ? customErrorHandler(error) : defaultMessage;
      toast.error(message);
    },
    onSuccess: (_data: TResponse, _variables: { data: TData; id: number }) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      queryClient.invalidateQueries({ queryKey: ['brands', queryKey] });
      toast.success(`${entityName} atualizado com sucesso!`);
    },
    retry: 3,
  });
}
